import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { cn } from '../lib/utils';

const MarkdownRenderer = ({
  content,
  className,
  variant = 'default'
}) => {
  const variantClasses = {
    default: 'prose prose-gray max-w-none',
    compact: 'prose prose-sm prose-gray max-w-none',
    large: 'prose prose-lg prose-gray max-w-none'
  };

  return (
    <div className={cn(
      variantClasses[variant],
      // Override default prose styles to work better with our design system
      'prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground',
      'prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground',
      'prose-blockquote:text-muted-foreground prose-blockquote:border-border',
      'prose-code:text-foreground prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md',
      'prose-pre:bg-muted prose-pre:text-foreground',
      'prose-hr:border-border',
      // Improve heading spacing and sizing
      'prose-h1:text-2xl prose-h1:font-bold prose-h1:mb-4 prose-h1:mt-6 first:prose-h1:mt-0',
      'prose-h2:text-xl prose-h2:font-semibold prose-h2:mb-3 prose-h2:mt-5 first:prose-h2:mt-0',
      'prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-2 prose-h3:mt-4 first:prose-h3:mt-0',
      'prose-h4:text-base prose-h4:font-medium prose-h4:mb-2 prose-h4:mt-3 first:prose-h4:mt-0',
      // Improve paragraph spacing
      'prose-p:mb-4 prose-p:leading-relaxed',
      // Improve list styling
      'prose-ul:mb-4 prose-ol:mb-4 prose-li:mb-1',
      // Make links work with our color system
      'prose-a:text-primary prose-a:underline hover:prose-a:text-primary/80',
      // Ensure proper overflow handling
      'overflow-y-auto break-words max-h-full',
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Custom renderers for specific elements
          h1: ({ children, ...props }) => (
            <h1 className="text-2xl font-bold text-foreground mb-4 mt-6 first:mt-0 break-words" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-xl font-semibold text-foreground mb-3 mt-5 first:mt-0 break-words" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-lg font-semibold text-foreground mb-2 mt-4 first:mt-0 break-words" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-base font-medium text-foreground mb-2 mt-3 first:mt-0 break-words" {...props}>
              {children}
            </h4>
          ),
          p: ({ children, ...props }) => (
            <p className="text-foreground mb-4 leading-relaxed break-words" {...props}>
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul className="text-foreground mb-4 space-y-1 list-disc list-inside break-words" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="text-foreground mb-4 space-y-1 list-decimal list-inside break-words" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-foreground mb-1 break-words" {...props}>
              {children}
            </li>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-4 border-border pl-4 my-4 italic text-muted-foreground break-words" {...props}>
              {children}
            </blockquote>
          ),
          code: ({ children, ...props }) => (
            <code 
              className="bg-muted text-foreground px-1.5 py-0.5 rounded-md text-sm font-mono break-all" 
              {...props}
            >
              {children}
            </code>
          ),
          pre: ({ children, ...props }) => (
            <pre 
              className="bg-muted text-foreground p-3 rounded-md text-sm font-mono overflow-x-auto my-4 break-words" 
              {...props}
            >
              {children}
            </pre>
          ),
          strong: ({ children, ...props }) => (
            <strong className="font-semibold text-foreground break-words" {...props}>
              {children}
            </strong>
          ),
          em: ({ children, ...props }) => (
            <em className="italic text-foreground break-words" {...props}>
              {children}
            </em>
          ),
          hr: ({ ...props }) => (
            <hr className="my-6 border-border" {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer; 